import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FormDialog from '../../common/FormDialog';
import { assessmentAPI } from '../../../services/api';

const COMPONENTS = [
  { value: 'examination', label: 'Examination (Default)' },
  { value: 'continuous_assessment', label: 'Continuous Assessment' },
  { value: 'rubric', label: 'Rubric' },
];

const buildTypeRow = (type) => ({
  assessment_type_id: type.id,
  max_marks: 100,
  weight_percentage: '',
  sequence_order: '',
  assessment_component: 'examination',
  include_in_final: true,
});

const BulkAssessmentConfigDialog = ({
  open,
  onClose,
  onDone,
  sortedClassStreamSubjectPapers,
  assessmentTypes,
  terms,
  existingConfigKeys = new Set(),
  allConfigurations = [],
  editMode = false,
  isMobile = false,
}) => {
  const [term, setTerm] = useState(null);
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear());
  const [dueDate, setDueDate] = useState('');
  const [isCompulsory, setIsCompulsory] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [typeRows, setTypeRows] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, skipped: 0, errors: [] });

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setTerm(null);
      setAcademicYear(new Date().getFullYear());
      setDueDate('');
      setPolicy(null);
      setProgress({ done: 0, total: 0, skipped: 0, errors: [] });
      if (!editMode) setTypeRows(assessmentTypes.map(buildTypeRow));
      else setTypeRows([]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // In edit mode, pre-populate typeRows from existing configs for selected term/year
  useEffect(() => {
    if (!editMode || !term || !academicYear || !allConfigurations.length) return;
    const filtered = allConfigurations.filter(
      (c) => String(c.term_id) === String(term.id) && String(c.academic_year) === String(academicYear)
    );
    // Build one row per assessment type, using first matching config's values
    setTypeRows(assessmentTypes.map((type) => {
      const match = filtered.find((c) => String(c.assessment_type_id) === String(type.id));
      return match ? {
        assessment_type_id: type.id,
        max_marks: match.max_marks ?? 100,
        weight_percentage: match.weight_percentage ?? '',
        sequence_order: match.sequence_order ?? '',
        assessment_component: match.assessment_component || 'examination',
        include_in_final: Boolean(match.include_in_final ?? true),
      } : buildTypeRow(type);
    }));
  }, [editMode, term, academicYear, allConfigurations, assessmentTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch policy when scope changes (use first paper as representative)
  useEffect(() => {
    if (!term || !academicYear || sortedClassStreamSubjectPapers.length === 0) {
      setPolicy(null);
      return;
    }
    const firstPaper = sortedClassStreamSubjectPapers[0];
    assessmentAPI
      .validateWeights({
        class_stream_subject_paper_id: firstPaper.id,
        academic_year: academicYear,
        term_id: term.id,
      })
      .then((res) => setPolicy(res?.data?.policy || null))
      .catch(() => setPolicy(null));
  }, [term, academicYear, sortedClassStreamSubjectPapers]);

  const contributionModel = policy?.contribution_model || 'weighted_sum_100';
  const isSingle100 = contributionModel === 'single_100';

  const totalWeight = useMemo(
    () =>
      typeRows
        .filter((r) => r.include_in_final)
        .reduce((sum, r) => sum + (Number(r.weight_percentage) || 0), 0),
    [typeRows]
  );

  const weightOver100 = totalWeight > 100.01;

  const weightValid = useMemo(() => {
    const included = typeRows.filter((r) => r.include_in_final);
    if (included.length === 0) return false;
    if (isSingle100) return included.length === 1 && Number(included[0].weight_percentage) === 100;
    return Math.abs(totalWeight - 100) < 0.01;
  }, [isSingle100, typeRows, totalWeight]);

  const updateRow = (idx, field, value) => {
    setTypeRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const totalCombinations = sortedClassStreamSubjectPapers.length * assessmentTypes.length;
  const alreadyConfiguredCount = useMemo(() => {
    if (!term || !academicYear) return 0;
    let count = 0;
    for (const paper of sortedClassStreamSubjectPapers)
      for (const type of assessmentTypes)
        if (existingConfigKeys.has(`${paper.id}-${type.id}-${term.id}-${academicYear}`)) count++;
    return count;
  }, [term, academicYear, sortedClassStreamSubjectPapers, assessmentTypes, existingConfigKeys]);

  const allAlreadyConfigured = totalCombinations > 0 && alreadyConfiguredCount === totalCombinations;

  const canSubmit =
    !!term &&
    !!academicYear &&
    typeRows.every((r) => r.max_marks !== '' && (!r.include_in_final || r.weight_percentage !== '')) &&
    weightValid && !weightOver100 &&
    (editMode || !allAlreadyConfigured);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (editMode) {
      // Build bulk update payload: find existing config IDs for each paper+type combo
      const configurations = [];
      for (const paper of sortedClassStreamSubjectPapers) {
        for (const row of typeRows) {
          const existing = allConfigurations.find(
            (c) => String(c.class_stream_subject_paper_id) === String(paper.id) &&
              String(c.assessment_type_id) === String(row.assessment_type_id) &&
              String(c.term_id) === String(term.id) &&
              String(c.academic_year) === String(academicYear)
          );
          if (!existing) continue;
          configurations.push({
            id: existing.id,
            max_marks: Number(row.max_marks),
            weight_percentage: row.include_in_final ? Number(row.weight_percentage) : 0,
            sequence_order: row.sequence_order !== '' ? Number(row.sequence_order) : null,
            assessment_component: row.assessment_component || 'examination',
            due_date: dueDate || null,
            is_compulsory: Boolean(isCompulsory),
            include_in_final: Boolean(row.include_in_final),
            is_active: Boolean(isActive),
          });
        }
      }
      setSaving(true);
      try {
        await assessmentAPI.bulkUpdateAssessmentConfigurations({ configurations });
        onDone({ errors: [], skipped: 0, total: configurations.length });
      } catch (err) {
        onDone({ errors: [err?.response?.data?.message || 'Update failed'], skipped: 0, total: 0 });
      } finally {
        setSaving(false);
      }
      return;
    }

    const configurations = [];
    for (const paper of sortedClassStreamSubjectPapers) {
      for (const row of typeRows) {
        configurations.push({
          class_stream_subject_paper_id: Number(paper.id),
          assessment_type_id: Number(row.assessment_type_id),
          academic_year: Number(academicYear),
          term_id: Number(term.id),
          max_marks: Number(row.max_marks),
          weight_percentage: row.include_in_final ? Number(row.weight_percentage) : 0,
          sequence_order: row.sequence_order !== '' ? Number(row.sequence_order) : null,
          assessment_component: row.assessment_component || 'examination',
          due_date: dueDate || null,
          is_compulsory: Boolean(isCompulsory),
          include_in_final: Boolean(row.include_in_final),
          is_active: Boolean(isActive),
        });
      }
    }

    setSaving(true);
    setProgress({ done: 0, total: configurations.length, skipped: 0, errors: [] });

    try {
      const res = await assessmentAPI.createAssessmentConfiguration({ configurations });
      const results = res?.data?.data ?? [];
      const skipped = results.filter((r) => r.status === 'exists').length;
      const errors = results.filter((r) => r.status === 'failed').map((r) => r.error || 'Unknown error');
      setProgress({ done: configurations.length, total: configurations.length, skipped, errors });
      onDone({ errors, skipped, total: configurations.length });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Bulk create failed';
      setProgress({ done: 0, total: configurations.length, skipped: 0, errors: [msg] });
      onDone({ errors: [msg], skipped: 0, total: configurations.length });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={editMode ? 'Bulk Edit Configurations' : 'Bulk Add Configurations'}
      saving={saving}
      disabled={!canSubmit}
      isMobile={isMobile}
      maxWidth="md"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>

        {/* Scope */}
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Autocomplete
            fullWidth
            options={terms}
            value={term}
            onChange={(_, v) => {
              setTerm(v);
              if (v?.end_date) setDueDate(String(v.end_date).slice(0, 10));
            }}
            getOptionLabel={(t) => `${t.name || 'Term'} ${t.year || ''}`.trim()}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} label="Term" required />}
          />
          <TextField
            fullWidth type="number" label="Academic Year"
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            inputProps={{ min: 2020, max: new Date().getFullYear() + 1 }}
            required
          />
          <TextField
            fullWidth type="date" label="Due Date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <Box display="flex" gap={2}>
          <FormControlLabel
            control={<Switch checked={isCompulsory} onChange={(e) => setIsCompulsory(e.target.checked)} />}
            label="Compulsory"
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Active"
          />
        </Box>

        {/* Policy info */}
        {policy && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Policy: <strong>{policy.policy_code}</strong> — model:{' '}
            <strong>{policy.contribution_model}</strong>
            {policy.strict_weight_validation ? ', strict weights' : ''}
            {isSingle100 && (
              <> · Only one assessment should be included in final with 100% weight.</>
            )}
          </Alert>
        )}

        {/* Per-type weight table */}
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Assessment Type Weights
            {term && academicYear && (
              <Chip
                size="small"
                sx={{ ml: 1 }}
                label={`Total included: ${totalWeight.toFixed(1)}%`}
                color={weightValid ? 'success' : 'warning'}
              />
            )}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Max Marks</TableCell>
                <TableCell>Weight %</TableCell>
                {!isSingle100 && <TableCell>Seq Order</TableCell>}
                <TableCell>Component</TableCell>
                <TableCell>In Final</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {typeRows.map((row, idx) => {
                const type = assessmentTypes.find((t) => t.id === row.assessment_type_id);
                return (
                  <TableRow key={row.assessment_type_id}>
                    <TableCell>
                      <Tooltip title={type?.assessment_name || ''}>
                        <Typography variant="body2" fontWeight={600}>
                          {type?.assessment_code || type?.short_name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number"
                        value={row.max_marks}
                        onChange={(e) => updateRow(idx, 'max_marks', e.target.value)}
                        inputProps={{ min: 0, max: 1000, step: 0.01, style: { width: 70 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number"
                        value={row.weight_percentage}
                        onChange={(e) => updateRow(idx, 'weight_percentage', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01, style: { width: 70 } }}
                        disabled={!row.include_in_final}
                      />
                    </TableCell>
                    {!isSingle100 && (
                      <TableCell>
                        <TextField
                          size="small" type="number"
                          value={row.sequence_order}
                          onChange={(e) => updateRow(idx, 'sequence_order', e.target.value)}
                          inputProps={{ min: 1, max: 200, style: { width: 60 } }}
                          placeholder="—"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Component</InputLabel>
                        <Select
                          label="Component"
                          value={row.assessment_component}
                          onChange={(e) => updateRow(idx, 'assessment_component', e.target.value)}
                        >
                          {COMPONENTS.map((c) => (
                            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={row.include_in_final}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setTypeRows((prev) => prev.map((r, i) =>
                            i === idx ? { ...r, include_in_final: checked, weight_percentage: checked ? r.weight_percentage : '' } : r
                          ));
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {weightOver100 && (
            <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
              Total weight exceeds 100% ({totalWeight.toFixed(1)}%) — reduce weights before saving.
            </Typography>
          )}
          {!weightOver100 && !weightValid && term && academicYear && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
              {isSingle100
                ? 'single_100 policy: exactly one included assessment must have 100% weight.'
                : `Included weights sum to ${totalWeight.toFixed(1)}% — must equal 100%.`}
            </Typography>
          )}
        </Box>

        {/* Summary */}
        <Typography variant="body2" color="text.secondary">
          {editMode
            ? <>Will update configs for <strong>{sortedClassStreamSubjectPapers.length} papers × {assessmentTypes.length} types</strong> matching selected term/year.</>
            : <>Will create up to{' '}<strong>{sortedClassStreamSubjectPapers.length} papers × {assessmentTypes.length} types = {totalCombinations} configs</strong>{alreadyConfiguredCount > 0 && ` (${alreadyConfiguredCount} already exist and will be skipped)`}.</>}
        </Typography>

        {!editMode && allAlreadyConfigured && (
          <Alert severity="info">All configurations already exist for this term and year.</Alert>
        )}

        {/* Progress */}
        {saving && (
          <Box>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Submitting {progress.total} configurations…
            </Typography>
          </Box>
        )}

        {!saving && progress.done > 0 && progress.errors.length > 0 && (
          <Typography variant="caption" color="error">
            {progress.errors.length} failed: {progress.errors.slice(0, 3).join('; ')}
            {progress.errors.length > 3 ? '…' : ''}
          </Typography>
        )}

      </Stack>
    </FormDialog>
  );
};

export default BulkAssessmentConfigDialog;
